import React, { memo, useEffect, useRef, useState } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import { createSelector } from 'reselect'
import cx from 'classnames'

import { Grid, Typography, makeStyles, IconButton, CircularProgress } from '@material-ui/core'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

import Header from '~/components/Header'
import SubHeader from '~/components/Header/SubHeader'
import { useLiterals } from '~/components/Literals'
import SegmentationFilter from '~/components/StoreList/SegmentationFilter'

import { TYPES } from '~/actions'
import track from '~/track'
import { selectSegmentation } from '~/selectors'

// styles (mui)
const useStyles = makeStyles((theme) => ({
  header: {
    width: '100vw',
    background: theme.palette.secondary.main,
  },
  wrapper: {
    zIndex: 102,
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    transition: '.3s ease',
  },
  headerBasket: {
    width: '100%',
    position: 'absolute',
    zIndex: 100,
  },
  headerBottom: {
    position: 'relative',
    zIndex: 99,
    height: 56,
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 16,
    paddingRight: 16,
  },
  content: {
    transition: '.3s ease',
    background: theme.palette.background.paper,
    overflowY: 'auto',
    '-webkit-overflow-scrolling': 'touch',
    ...theme.gofind.scrollBar,
    '&:after': {
      content: '""',
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      height: '100vh',
      background: '#fff',
    },
  },

  toggleMap: ({ view }) => ({
    padding: '16px 0 8px',
    position: 'relative',
    transition: 'all 0.3s',
    backgroundColor: '#fff',

    display: view === TYPES.VIEW_PRODUCTS ? 'none' : 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '&.-full': {
      paddingBottom: 0,
      marginBottom: '0px',
    },

    '&:after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  }),
  toggleMapIcon: {
    fill: '#000',
    verticalAlign: 'middle',
  },
  toggleMapButton: {
    backgroundColor: '#FFF',
    border: '1px solid #EEE',
    position: 'absolute',
    top: 0,
    transform: 'translateY(-50%)',
  },
  overlay: {
    zIndex: 0,
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  overlay2: {
    width: '100%',
    height: '100vh',
    background: '#000',
    position: 'fixed',
    opacity: 0.6,
    zIndex: 1001,
  },
}))

const selectState = createSelector(
  (state) => state.app.mobileBottomShow,
  (state) => state.app.view,
  (state) => state.app.storesLoading,
  (state) => state.app.isMobileStoreInformationsFullHeight,
  (showBottom, view, storesLoading, isMobileStoreInformationsFullHeight) => ({
    showBottom,
    view,
    storesLoading,
    isMobileStoreInformationsFullHeight,
  }),
)

const getMapSize = (viewport) => {
  const isLowHeight = viewport.height < 700

  return viewport.height * (isLowHeight ? 0.5 : 0.6) + 56
}

function Mobile({ children, config, dispatch, handleToggle, position, state, viewport }) {
  const segmentation = useSelector(selectSegmentation)
  const hasSegmentation = !!Object.keys(segmentation || {}).length
  const { view, showBottom, storesLoading, isMobileStoreInformationsFullHeight } = useSelector(
    selectState,
    shallowEqual,
  )
  const { getLiteral } = useLiterals()

  const refHeader = useRef(null)
  const [searchFieldHaveBeenFocus, setSearchFieldHaveBeenFocus] = useState(false)

  const classes = useStyles({ view })
  const isLowHeight = viewport.height < 700

  useEffect(() => {
    if (view === TYPES.VIEW_PRODUCTS) dispatch({ current: 0, offset: 0 })
  }, [view])

  let dragTime
  let height = viewport.height * (isLowHeight ? 0.5 : 0.6)
  let diffHeight = viewport.height - (refHeader.current?.clientHeight || 0)

  if (view === TYPES.VIEW_STORE_DETAILS) {
    height = viewport.height * (isLowHeight ? 0.5 : 0.6) + 56
  }

  if (view === TYPES.VIEW_SEARCH_PRODUCTS) {
    diffHeight = viewport.height - (refHeader.current?.clientHeight || 0)
  }

  const handleTouchStart = (e) => {
    dispatch({
      initial: e.type === 'touchstart' ? e.touches[0].clientY - state.offset : e.clientY - state.offset,
      dragEnd: false,
      active: true,
    })
  }

  const handleTouchEnd = () => {
    if (Math.abs(state.current - state.offset) < 20) return

    setTimeout(() => {
      const target = state.current - state.offset < 60 ? 0 : getMapSize(viewport)

      dispatch({
        initial: state.current,
        active: false,
        current: target,
        offset: target,
        dragEnd: true,
      })
    }, 100)
  }

  const handleTouchMove = (e) => {
    e.persist()

    window.requestAnimationFrame(() => {
      dragTime += 1

      if (dragTime < 2) return

      if (view === TYPES.VIEW_PRODUCTS) return

      if (state.active) {
        let current = 0

        if (e.type === 'touchmove') {
          current = e.touches[0].clientY - state.initial
        } else {
          current = e.clientY - state.initial
        }

        dispatch({ current })
      }
    })
  }

  const handleTrack = () => {
    if (view === 'VIEW_PRODUCTS') {
      if (state.current === 0) {
        return track('expandirMapaProduto', { category: 'mapa', name: 'products' })
      }

      return track('cliqueVerTodosOsProdutos', { category: 'mapa', name: 'all' })
    }

    if (state.current === 0) {
      return track('expandirMapaLoja', { category: 'mapa', name: 'store' })
    }

    return track('cliqueVerTodasAsLojas', { category: 'mapa', name: 'all' })
  }

  const handleIncludeView = (view) => {
    return [TYPES.VIEW_PRODUCTS, TYPES.VIEW_SEARCH_PRODUCTS].includes(view)
  }

  const Icon = state.current > 0 ? ExpandLessIcon : ExpandMoreIcon
  const isStateCurrentEqualsMapSize = state.current === getMapSize(viewport)

  const handlers = {
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
    onTouchMove: handleTouchMove,
  }

  return (
    <>
      {!isStateCurrentEqualsMapSize && <Grid className={classes.overlay} onClick={handleToggle} />}

      {searchFieldHaveBeenFocus && <Grid className={classes.overlay2} />}

      <Grid className={classes.header} ref={refHeader} style={{ position: handleIncludeView(view) && 'relative' }}>
        <Header
          config={config}
          position={position}
          handleToggle={handleToggle}
          searchFieldHaveBeenFocus={searchFieldHaveBeenFocus}
          setSearchFieldHaveBeenFocus={setSearchFieldHaveBeenFocus}
        />

        {view === TYPES.VIEW_PRODUCTS && <SubHeader config={config} position={position} handlers={handlers} />}
      </Grid>

      {/* se storeinformation aberto esconte o trecho abaixo */}
      {view !== TYPES.VIEW_EMPTY && !isMobileStoreInformationsFullHeight && (
        <>
          <Grid
            id="bottom-wrap"
            className={cx(classes.wrapper, { [classes.showBottom]: showBottom })}
            style={{
              position: handleIncludeView(view) && 'relative',
              height: handleIncludeView(view) && '100%',
              transform: !handleIncludeView(view) && 'translate3d(0px, ' + state.current + 'px, 0)',
            }}
          >
            <Grid {...handlers} onClick={handleTrack}>
              {!handleIncludeView(view) && (
                <Grid
                  className={`${classes.toggleMap} ${isStateCurrentEqualsMapSize ? '-full' : 0}`}
                  onClick={handleToggle}
                >
                  <IconButton
                    size="small"
                    classes={{ root: classes.toggleMapButton }}
                    style={{ backgroundColor: '#fff' }}
                  >
                    {storesLoading ? <CircularProgress size={24} /> : <Icon className={classes.toggleMapIcon} />}
                  </IconButton>
                  <Typography>
                    {storesLoading
                      ? `${getLiteral('1503')}...`
                      : isStateCurrentEqualsMapSize
                        ? getLiteral('2546')
                        : getLiteral('2545')}
                  </Typography>
                </Grid>
              )}

              {view !== TYPES.VIEW_PRODUCTS && <SubHeader config={config} position={position} handlers={handlers} />}

              {view === TYPES.VIEW_STORES && hasSegmentation && <SegmentationFilter />}
            </Grid>

            {[TYPES.VIEW_PRODUCTS, TYPES.VIEW_SEARCH_PRODUCTS, TYPES.VIEW_STORES].map((key) => {
              if (view === key) {
                return (
                  <Grid
                    key={key}
                    className={classes.content}
                    style={{
                      height: view === TYPES.VIEW_STORES ? height : diffHeight,
                    }}
                  >
                    {children}
                  </Grid>
                )
              }
            })}

            {view === TYPES.VIEW_STORE_DETAILS && (
              <Grid className={cx(classes.content, classes.storeDetails)} style={{ height }} {...handlers}>
                {children}
              </Grid>
            )}
          </Grid>
        </>
      )}
    </>
  )
}

export default memo(Mobile)
